/**
 * Section Routes Utility
 *
 * Maps URL-friendly slug paths to Hero content keys used by the app's
 * internal navigation system (SidePanel → Hero switch/case pattern).
 *
 * Usage:
 *   - URL: /app/dashboard → heroContent: "Dashboard"
 *   - URL: /app/training → heroContent: "Training Center"
 *   - heroContent: "Dashboard" → URL slug: "dashboard"
 */

// URL slug → Hero content key
const SECTION_MAP = {
  dashboard: "Dashboard",
  "simulation-messages": "Simulation Messages",
  "simulation-profile": "Simulation Profile",
  "recent-simulations": "Recent Simulations",
  reports: "Reports",
  "email-report": "Generate Email Report",
  "risk-scores": "Generate Risk Scores",
  "send-email": "Send Email",
  account: "Account & Billing",
  subscriptions: "Subscriptions",
  payments: "Payments",
  settings: "Settings",
  "settings-change-password": "Settings:ChangePassword",
  "settings-mfa": "Settings:MFA",
  "settings-disable-mfa": "Settings:DisableMFA",
  "settings-sms-consent": "Settings:SmsConsent",
  "settings-notifications": "Settings:Notifications",
  members: "Member User Info",
  "user-activity-monitoring": "User Activity Monitoring",
  "members-create": "Member User Info:Create",
  "members-update": "Member User Info:Update",
  "members-details": "Member User Info:Details",
  "members-resend-verification": "Member User Info:ResendVerification",
  "payments-methods": "Payments:Methods",
  "payments-add-card": "Payments:AddCard",
  "payments-history": "Payments:History",
  groups: "Groups & Organizations",
  "service-plans": "Service Plans",
  "email-scan": "Manual Entry",
  "forwarded-emails": "Forwarded Emails",
  "email-analysis": "Email Analysis",
  "phone-check": "Phone Number Check",
  "sms-scan": "Manual Input",
  "text-conversation": "Text Conversation",
  "forwarded-messages": "Forwarded Texts",
  "social-media": "Social Media Scams",
  "url-check": "Social Media Posts",
  screenshot: "Screenshot Analysis",
  "create-schedule": "Create Simulation Schedule",
  "run-schedule": "Execute Simulation Schedule",
  "blog-generator": "Generate Blogs",
  announcements: "Manage Announcements",
  admin: "Admin Dashboard",
  "user-subscription-audit": "User Subscription Audit",
  help: "How do I...?",
  "getting-started": "Getting Started",
  support: "ContactSupport",
  notifications: "Notifications",
  training: "Training Center",
  "alerts-hub": "Alerts Intelligence Hub",
  "simulation-data": "Generate Simulation Data",
  "shield-reports": "Shield Reports",
  export: "Export Data",
  "demo-admin": "Demo Users",
};

// Reverse map: Hero content key → URL slug
const CONTENT_TO_SLUG = Object.fromEntries(
  Object.entries(SECTION_MAP).map(([slug, content]) => [content, slug]),
);

/**
 * Convert a URL slug to a Hero content key.
 * Returns "Dashboard" if the slug is not recognized.
 */
export function slugToHeroContent(slug) {
  if (!slug) return "Dashboard";
  return SECTION_MAP[slug.toLowerCase()] || "Dashboard";
}

/**
 * Convert a Hero content key to a URL slug.
 * Returns "dashboard" if the content key is not recognized.
 */
export function heroContentToSlug(contentKey) {
  if (!contentKey) return "dashboard";
  return CONTENT_TO_SLUG[contentKey] || "dashboard";
}

/**
 * Build a full app path from a Hero content key.
 * Example: "Training Center" → "/app/training"
 */
export function buildAppPath(contentKey) {
  const slug = heroContentToSlug(contentKey);
  return `/app/${slug}`;
}

export default SECTION_MAP;
